<template>
  <model-form
    ref="modelForm"
    :model="role"
    @change="handleRoleChange"
  >
    <div class="row q-col-gutter-x-md q-mb-md">
      <div class="col-12 col-sm-6">
        <model-form-input
          field="name"
          label="Name"
          :rules="[val => !!val || 'Field is required', validateIsRoleNameUsed, validateIsRoleNameReserved]"
          clearable
          autofocus
        />
      </div>
      <div class="col-12 col-sm-6">
        <model-form-checkbox
          field="autoAssignAdmin"
          label="Automatically assign to new admin users"
        />
        <model-form-checkbox
          field="autoAssignEmployee"
          label="Automatically assign to new employee users"
        />
      </div>
    </div>

    <q-tabs
      v-model="tab"
      dense
      class="text-grey"
      active-color="primary"
      indicator-color="primary"
      align="justify"
    >
      <q-tab
        :name="TABS.PERMISSIONS"
        :label="`Permissions (${role.permissionCount})`"
      />
      <q-tab
        :name="TABS.USERS"
        :label="`Users (${role.userCount})`"
      />
    </q-tabs>
    <q-tab-panels
      v-model="tab"
      animated
    >
      <q-tab-panel
        :name="TABS.PERMISSIONS"
      >
        <model-table
          :title="`Permissions (${role.permissionCount})`"
          submodel
          add-label="Permissions"
          :collection="role.rolePermissions"
          :columns="[
            { field: 'category' },
            { field: 'label' },
            { label: 'Permits Employee Level Users', field: 'permitsEmployeeUsers', component: 'checkmark-or-negative' },
            { label: 'Permits Admin Level Users', field: 'permitsAdminUsers', component: 'checkmark-or-negative' },
            { name: 'Restricted Records', field: rolePermission => restrictedRecordsSummary(rolePermission) },
            { name: 'actions', align: 'right' }
          ]"
          @add="addRolePermission"
        >
          <role-permission-form
            :handled-actions="['edit']"
            @change="saveRolePermissionChange"
            @remove="removeRolePermissionAction"
          />
        </model-table>
      </q-tab-panel>
      <q-tab-panel
        :name="TABS.USERS"
      >
        <model-table
          :title="`Employees (${role.userCount})`"
          submodel
          add-label="Employees"
          :collection="role.users"
          :columns="[
            { field: 'id' },
            { name: 'photo', field: 'photoSmallUrl', component: 'img' },
            { field: 'name', classes: 'sensitive-column' },
            { field: 'email', classes: 'sensitive-column' },
            { name: 'actions', align: 'right' }
          ]"
          :show-edit="false"
          :show-add="$isPermitted($permissions.Security.ManageUserRoles)"
          @add="$refs.usersSelectDialog.show()"
        />
      </q-tab-panel>
    </q-tab-panels>
    <select-role-users-dialog
      ref="usersSelectDialog"
      :role="role"
    />
    <select-role-permissions-dialog
      ref="selectRolePermissionsSelectDialog"
      :role="role"
    />
  </model-form>
</template>

<script>
import Role from 'src/models/access-control/Role'
import Model from 'src/models/model'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import ModelTable from 'src/modules/admin/components/ModelTable'
import SelectRolePermissionsDialog from 'src/modules/admin/role/SelectRolePermissionsDialog'
import SelectRoleUsersDialog from 'src/modules/admin/role/SelectRoleUsersDialog'
import RolePermissionForm from 'src/modules/admin/role-permission/RolePermissionForm'
import { resetMenuList } from 'src/modules/admin/search/MenuSearchComponent'

const TABS = {
  PERMISSIONS: 'permissions',
  USERS: 'users'
}

export default {
  name: 'RoleForm',
  components: {
    ModelForm,
    ModelTable,
    RolePermissionForm,
    SelectRolePermissionsDialog,
    SelectRoleUsersDialog
  },
  data () {
    return { tab: TABS.PERMISSIONS }
  },
  models: {
    roles: 'default',
    role: {
      users: {
        fields: ['id', 'photo', 'firstName', 'lastName', 'email']
      },
      rolePermissions: {
        id: true,
        action: true,
        conditionsRequirements: true,
        restrictsRecords: true,
        restrictableModelNames: true,
        rolePermissionRecordRestrictions: true,
        permission: 'default'
      }
    }
  },
  computed: {
    existingRoleNames () {
      return _.map(this.roles.data, 'name')
    }
  },
  created () {
    _.extend(this, { TABS })
  },
  methods: {
    async handleRoleChange () {
      // Refresh frontend permissions
      await Model.context.fetch(true)
      // TODO: move this elsewhere
      resetMenuList()
    },
    removeRolePermissionAction (action) {
      const rolePermission = _.find(this.role.rolePermissions.data, { action })
      if (rolePermission) {
        this.role.rolePermissions.remove(rolePermission)
      }
    },
    addRolePermission () {
      this.$refs.selectRolePermissionsSelectDialog.show()
    },
    editRolePermission (rolePermission) {
      this.$refs.editRolePermissionsDialog.show(rolePermission)
    },
    saveRolePermissionChange () {
      this.role.save()
    },
    restrictedRecordsSummary (rolePermission) {
      if (_.isEmpty(rolePermission.restrictableModelNames)) {
        return ''
      }
      return _.map(rolePermission.restrictableModelNames, (modelName) => {
        return `${modelName}: ${rolePermission.restrictedRecordCountByModelName[modelName] || 0}`
      }).join(', ')
    },
    validateIsRoleNameReserved (name) {
      if (!Role.isReservedRoleName(name)) return true
      return `Name "${name}" is reserved`
    },
    validateIsRoleNameUsed (name) {
      if (this.role.isRemote) return true
      const downcasedUsedNames = _.map(this.existingRoleNames, _.toLower)
      if (!_.includes(downcasedUsedNames, _.toLower(name))) return true
      return 'A Role already exists with this name'
    }
  }
}
</script>
